import type { StepPageLocation, UrlMatcher } from './types';

/**
 * Matching de URL en CLIENTE (portado del backend `url-matcher.ts`). Lo usa el
 * motor composite para el targeting de página POR-PASO: como cada paso puede
 * apuntar a una página distinta (tours multi-página estilo Pendo), el SDK evalúa
 * la coincidencia en el navegador en cada navegación, no solo el backend.
 *
 * El runtime manda `window.location.href` (URL completa) y el editor expresa
 * las reglas como PATHS (`/envios`) o URLs con protocolo. Se prueban TODOS los
 * candidatos: la URL completa, el path derivado (`pathname+search+hash`) y, en
 * apps con hash routing (`/#/envios`), la ruta del hash (`/envios`).
 *
 * Errores de regex → `false` (no lanza): el patrón se validó al crear, pero una
 * fila corrupta no debe romper el render de una guía ajena.
 */
export function urlCandidates(pageUrl: string): string[] {
  const out = [pageUrl];
  try {
    const u = new URL(pageUrl);
    out.push(`${u.pathname}${u.search}${u.hash}`);
    if (u.hash.startsWith('#/')) out.push(u.hash.slice(1));
  } catch {
    // no es una URL absoluta: se compara tal cual
  }
  return out;
}

export function matchesUrl(matcher: UrlMatcher, pageUrl: string): boolean {
  const candidates = urlCandidates(pageUrl);
  switch (matcher.type) {
    case 'exact':
      return candidates.some((c) => c === matcher.pattern);
    case 'prefix':
      return candidates.some((c) => c.startsWith(matcher.pattern));
    case 'regex':
      try {
        const re = new RegExp(matcher.pattern);
        return candidates.some((c) => re.test(c));
      } catch {
        return false;
      }
  }
}

/** ¿El paso composite debe mostrarse en esta URL? Sitewide siempre; page → patrón. */
export function stepMatchesPage(
  loc: StepPageLocation | null | undefined,
  pageUrl: string,
): boolean {
  if (!loc || loc.scope === 'sitewide' || !loc.pattern) return true;
  return matchesUrl({ type: loc.matchType ?? 'prefix', pattern: loc.pattern }, pageUrl);
}
